// CRITICAL: Import Firebase loader FIRST before any other imports that might use Firebase
// This ensures the firebase/auth component registration side effects execute properly
// The loader uses synchronous imports to guarantee shared internal state
import { initializeFirebase } from '@/firebase/firebase-loader';

import { createApp } from "vue";
// Early import to capture true bootstrap time from the start of app script execution
import { performanceMonitor } from '@/router/core/performance-monitor';
import { createPinia } from "pinia";
import App from "./App.vue";
import './index.css';
import './css/form-errors.css';
import { router } from "@/router";

import InstantSearch from 'vue-instantsearch/vue3/es';
import { createGtm } from '@gtm-support/vue-gtm';
import { createHead } from '@vueuse/head';
import descope, { getSdk } from "@descope/vue-sdk";
import UnsavedChangesPlugin from "@/plugins/unsavedChanges";
import CircleFlags from "vue-circle-flags";
import "vue-circle-flags/dist/vue-circle-flags.css";

// Environment validation (development only)
if (import.meta.env.MODE === 'development') {
    import('./utils/environment-validator').then(({ printEnvironmentReport }) => {
        printEnvironmentReport();
    }).catch(error => {
        console.warn('Could not load environment validator:', error);
    });
    
    // Import Firebase production issue detector (runs automatically)
    import('./utils/firebase-production-detector').catch(error => {
        console.warn('Could not load Firebase production detector:', error);
    });
}

// Enable performance monitoring as early as possible in development
try {
    if (import.meta.env.DEV && !performanceMonitor.enabled) {
        performanceMonitor.enable();
    }
} catch (error) {
    console.error('[Performance Monitor] Error enabling performance monitoring:', error);
}

// Start end-to-end bootstrap timer (from module load → first page ready)
const __bootstrapTimingId = performanceMonitor.startTiming('app_bootstrap', { phase: 'script_start' });

const pinia = createPinia();
const head = createHead();

// GTM Configuration - now enabled in both production AND development for testing
// Native GTM scripts are loaded in index.html for early/complete coverage
// The Vue GTM plugin provides additional SPA route tracking integration
const gtmId = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID as string | undefined;
const gtmIdValid = typeof gtmId === 'string' && /^(GTM|G)-[0-9A-Z]+$/.test(gtmId);
// Enable GTM in development if VITE_ENABLE_GTM_DEV is set, or always in production
const enableGtmInDev = import.meta.env.VITE_ENABLE_GTM_DEV === 'true' || import.meta.env.DEV;
const shouldEnableGtm = gtmIdValid && (import.meta.env.PROD || enableGtmInDev);

async function initializeApp() {
    // Measure major bootstrap phases for better attribution
    const timePhase = async <T>(name: string, fn: () => Promise<T>) => {
        return performanceMonitor.timeAsyncOperation(`bootstrap:${name}`, fn, { phase: name });
    };

    // CRITICAL: Initialize all Firebase services before anything else
    // Firebase modules are already imported synchronously at the top of this file
    // This call initializes the service instances (Auth, Firestore, etc.)
    console.log('🔥 Initializing Firebase services...');
    await timePhase('firebase_init', async () => {
        await initializeFirebase();
    });
    console.log('✅ Firebase services initialized');

    const app = createApp(App)
        .use(pinia)
        .use(router)
        .use(InstantSearch)
        .use(descope, {
            projectId: 'P2ujfS41NPXLTVb7bgvPyoA7683B',
            // add these to avoid cookie-only reliance
            persistTokens: true,           // persist across reloads
        })
        .use(head)
        .use(UnsavedChangesPlugin)
        .use(CircleFlags);

    // Register GTM Vue plugin for SPA route tracking
    // Note: Native GTM scripts are already loaded in index.html for early coverage
    if (shouldEnableGtm) {
        try {
            app.use(createGtm({ 
                id: gtmId as string, 
                vueRouter: router,
                debug: import.meta.env.DEV, // Enable debug logging in development
            }));
            console.log('✅ [GTM] Vue plugin initialized:', gtmId, import.meta.env.DEV ? '(debug mode)' : '');
        } catch (e) {
            console.warn('⚠️ [GTM] Failed to initialize GTM plugin:', e);
        }
    } else {
        if (!gtmIdValid) {
            console.warn('⚠️ [GTM] Not initialized: missing or invalid VITE_GOOGLE_TAG_MANAGER_ID');
            console.info('💡 [GTM] Set VITE_GOOGLE_TAG_MANAGER_ID in your .env file (e.g., GTM-P9M59L6G)');
        }
    }

    // ENHANCED BOOTSTRAP: Use parallel bootstrap optimizer
    await timePhase('private_label_bootstrap', async () => {
        // Import bootstrap optimizer for parallel execution
        try {
            // 🎭 CRITICAL: Check for demo domain FIRST before any other project resolution
            // This ensures demo mode is detected early and fake user is set up before router guards
            const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
            const isDemoDomain = hostname === 'demo.shir.ly' || hostname.includes('demo.shir.ly');
            
            // Import stores early for demo detection
            const { useProjectStore } = await import('@/store/project.store');
            const { useUserStore } = await import('@/store/user.store');
            const projectStore = useProjectStore();
            const userStore = useUserStore();
            const { useDemo } = await import('@/composables/useDemo');
            const demo = useDemo();
            
            // Import the router context resolution to get initial projectId
            const { projectType: detectedType, initialProjectId } = await import('@/router');
            const { projectContextManager } = await import('@/services/project-context-manager');
            
            // Check site config directly for demo projectId (more reliable than router)
            const siteConfig = projectContextManager.getSiteConfig();
            const siteConfigProjectId = siteConfig?.projectId;
            
            // Check if projectId is 'demo' (from site config, router, or store)
            const isDemoProjectId = siteConfigProjectId === 'demo' || 
                                   initialProjectId === 'demo' || 
                                   projectStore.projectId === 'demo';
            
            // Demo mode detection: domain OR projectId
            if (isDemoDomain || isDemoProjectId) {
                console.log('🎭 [Bootstrap] Demo mode detected:', {
                    domain: isDemoDomain,
                    hostname,
                    siteConfigProjectId,
                    routerProjectId: initialProjectId,
                    storeProjectId: projectStore.projectId,
                    isDemoProjectId
                });
                
                // Set demo project immediately
                projectStore.setState({ key: 'projectId', value: 'demo' });
                projectStore.setState({ key: 'isDemo', value: true });
                console.log('✅ [Bootstrap] Demo project set to "demo"');
                
                // Set up fake user immediately if not already authenticated
                if (!userStore.isAuthenticated) {
                    console.log('🎭 [Bootstrap] Setting up fake demo user...');
                    try {
                        const success = await demo.handleDemoAuthenticationBypass();
                        if (success) {
                            console.log('✅ [Bootstrap] Fake demo user setup complete');
                        } else {
                            console.error('❌ [Bootstrap] Failed to set up fake demo user');
                        }
                    } catch (error) {
                        console.error('❌ [Bootstrap] Error setting up fake demo user:', error);
                    }
                } else {
                    console.log('✅ [Bootstrap] User already authenticated, skipping fake user setup');
                }
                
                // Notify project context manager
                projectContextManager.notifyBootstrapComplete('demo', 'private_label', null);
                
                // Early return - demo mode is fully initialized
                return;
            }
            
            try {
                await demo.cleanupDemoArtifacts?.('bootstrap:non-demo');
            } catch (error) {
                console.warn('⚠️ [Bootstrap] Demo cleanup failed during non-demo startup:', error);
            }
            
            // Continue with normal project resolution for non-demo sites
            const { useApplicationStore } = await import('@/store/application.store');

            console.log('⚡ [Bootstrap] Using router-resolved context (0ms):', {
                projectType: detectedType,
                projectId: initialProjectId,
                source: 'router'
            });

            if (detectedType === 'private_label') {
                const applicationStore = useApplicationStore();
                // Ensure app stays in bootstrap state until project is resolved
                applicationStore.setState({ key: 'isAppReady', value: false });

                // Use the project info already resolved by router if available
                let detected: { projectId: string | null, projectInfo: any, source: string } = {
                    projectId: initialProjectId,
                    projectInfo: null,
                    source: 'router'
                };

                // If we don't have project info yet, resolve it from the site config
                if (!detected.projectId) {
                    const siteConfig = projectContextManager.getSiteConfig();
                    if (siteConfig) {
                        detected = {
                            projectId: siteConfig.projectId,
                            projectInfo: siteConfig,
                            source: 'site_config'
                        };
                    }
                }

                console.log('📊 [Bootstrap] Using optimized project context:', {
                    projectId: detected.projectId,
                    source: detected.source,
                    projectType: detectedType
                });
                if (detected?.projectId) {
                    projectStore.setState({ key: 'projectId', value: detected.projectId });

                    // Ensure we have complete project information for private label
                    if (detected.projectInfo) {
                        projectStore.setState({ key: 'projectInfo', value: detected.projectInfo as any });
                        console.log('✅ [Bootstrap] Project info loaded from context:', detected.projectInfo);
                    } else {
                        // If projectInfo isn't in context, fetch it explicitly for branding
                        console.log('🔄 [Bootstrap] Loading complete project information for branding...');
                        try {
                            const { getProjectInformation } = await import('@/store/helpers/project/getProjectInformation');
                            // For private label sites, use secret key since user is not authenticated yet during bootstrap
                            const fullProjectInfo = await getProjectInformation(detected.projectId, ['info'], true);
                            if (fullProjectInfo?.info) {
                                projectStore.setState({ key: 'projectInfo', value: fullProjectInfo.info as any });
                                console.log('✅ [Bootstrap] Complete project info loaded:', fullProjectInfo.info);
                                // Update detected.projectInfo for the notification
                                detected.projectInfo = fullProjectInfo.info;
                            }
                        } catch (error) {
                            console.warn('⚠️ [Bootstrap] Failed to load complete project info:', error);
                        }
                    }

                    console.log(`✅ [Bootstrap] Private label project detected early (${detected.source}):`, detected.projectId);
                    console.log('✅ [Bootstrap] ProjectId set in store:', projectStore.projectId);

                    // CRITICAL: Notify project context manager for synchronous access
                    const { projectContextManager } = await import('@/services/project-context-manager');
                    projectContextManager.notifyBootstrapComplete(
                        detected.projectId,
                        'private_label',
                        detected.projectInfo
                    );
                } else {
                    console.warn('⚠️ [Bootstrap] Failed to resolve private label project for domain:', window.location.origin);
                }
            } else {
                // White-label: try to extract projectId from the current route path early
                try {
                    const path = window.location.pathname || '/';
                    const match = path.match(/^\/([^/]+)(?:\/|$)/);
                    const candidate = match ? match[1] : '';
                    // Avoid common non-project roots
                    const invalid = new Set(['', 'login', 'signup', 'projects', 'profile', 'error', 'forbidden', 'not-found', 'home']);
                    if (candidate && !invalid.has(candidate)) {
                        projectStore.setState({ key: 'projectId', value: candidate });
                        console.log('✅ [Bootstrap] White label project detected from path:', candidate);
                    }
                } catch (e) {
                    console.warn('⚠️ [Bootstrap] White label project ID extraction failed:', e);
                }
            }
        } catch (e) {
            console.warn('⚠️ [Bootstrap] Private-label early detection skipped due to error:', e);
        }
    });

    // CRITICAL: Initialize authentication FIRST before router guards
    console.log('🔐 Initializing authentication system...');
    await timePhase('auth_init', async () => {
        try {
            // 🎭 Check if we're in demo mode - if so, skip Firebase Auth initialization
            const { useProjectStore } = await import('@/store/project.store');
            const { useUserStore } = await import('@/store/user.store');
            const projectStore = useProjectStore();
            const userStore = useUserStore();
            
            // Check if demo mode is already set up (from bootstrap phase)
            const isDemoProject = projectStore.projectId === 'demo' || projectStore.isDemo;
            const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
            const isDemoDomain = hostname === 'demo.shir.ly' || hostname.includes('demo.shir.ly');
            const isDemoMode = isDemoProject || isDemoDomain;
            
            if (isDemoMode && userStore.isAuthenticated) {
                console.log('🎭 [Auth Init] Demo mode detected and fake user already set up, skipping Firebase Auth');
                return; // Skip Firebase Auth initialization for demo mode
            }
            
            // Import Firebase Auth and user store
            const { Auth } = await import('@/firebase/auth');

            // Check Firebase Auth current user and initialize stores if authenticated
            // Firebase Auth may need async initialization in code-split builds
            let currentUser = null;
            
            try {
                // Try to get current user synchronously first
                // The Auth proxy will return null if auth isn't ready yet
                currentUser = Auth.currentUser;
            } catch (error: any) {
                // If Auth isn't ready yet, wait for async initialization
                // Check for various initialization error patterns
                const isInitError = error?.message?.includes('not been registered') || 
                                       error?.message?.includes('Component') ||
                                       error?.message?.includes('not initialized') ||
                                       error?.message?.includes('Auth not initialized');
                if (isInitError) {
                    console.log('🔄 Waiting for Firebase Auth initialization (async)...');
                    // Wait a bit for async initialization to complete
                    await new Promise(resolve => setTimeout(resolve, 200));
                    try {
                        currentUser = Auth.currentUser;
                    } catch (e) {
                        console.warn('⚠️ Firebase Auth still not ready after wait');
                    }
                } else {
                    throw error;
                }
            }

            // If no immediate user, wait a short time for Firebase Auth to initialize
            if (!currentUser) {
                console.log('🔄 Waiting for Firebase Auth initialization...');

                currentUser = await new Promise<any>((resolve) => {
                    const unsubscribe = Auth.onAuthStateChanged((user) => {
                        unsubscribe();
                        resolve(user);
                    });

                    // Timeout after 2 seconds to avoid hanging
                    setTimeout(() => {
                        unsubscribe();
                        resolve(null);
                    }, 2000);
                });
            }

            if (currentUser) {
                console.log('✅ Firebase Auth user detected:', currentUser.uid);

                // Initialize user store with Firebase Auth user
                userStore.setState({ key: 'authUser', value: currentUser });
                userStore.setState({ key: 'isAuthenticated', value: true });

                // In private_label development, force an immediate token refresh to pick up
                // custom claims for Firestore rules. This mitigates Incognito claim lag.
                try {
                    const { getProjectType } = await import('@/config/global-site-config');
                    const projectType = getProjectType();
                    const isPrivateLabelDev = projectType === 'private_label' && import.meta.env.DEV;
                    if (isPrivateLabelDev) {
                        await currentUser.getIdToken(true);
                    }
                } catch (e) {
                    console.warn('⚠️ [Bootstrap] Early token refresh failed:', e);
                }

                // Load user document from Firestore for complete user data
                try {
                    const { collection, doc, getDoc } = await import('firebase/firestore');
                    const { DB } = await import('@/firebase/db');
                    const userDoc = await getDoc(doc(collection(DB, 'users'), currentUser.uid));

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        userStore.setState({ key: 'user', value: userData as any });
                        console.log('✅ User document loaded from Firestore');
                    }
                } catch (firestoreError) {
                    console.warn('⚠️ Could not load user document from Firestore:', firestoreError);
                }

                // Get and process custom claims for RBAC
                try {
                    const idTokenResult = await currentUser.getIdTokenResult(false);
                    if (idTokenResult?.claims) {
                        userStore.setState({
                            key: 'customClaims',
                            value: idTokenResult.claims as any
                        });
                        console.log('✅ Custom claims loaded for RBAC');

                        // Derive Shirly admin flag from claims
                        try {
                            const claims: any = idTokenResult.claims || {};
                            const isShirlyAdmin = claims?.shirly === 'admin' || claims?.shirly === true;
                            userStore.setState({ key: 'isShirlyAdmin', value: Boolean(isShirlyAdmin) });
                            if (isShirlyAdmin) {
                                console.log('✅ Shirly admin detected from custom claims');
                            }
                        } catch (e) {
                            console.warn('⚠️ Could not derive Shirly admin from claims:', e);
                        }

                        // Trigger RBAC role update now that we have claims
                        const { useRBAC } = await import('@/composables/useRBAC');
                        const rbac = useRBAC();
                        await rbac.updateRole('manual');

                        console.log('✅ RBAC role initialized during bootstrap');
                    }
                } catch (claimsError) {
                    console.warn('⚠️ Could not load custom claims during bootstrap:', claimsError);
                }

                // Bind user data and load user projects
                try {
                    const { bindUser } = await import('@/utils/auth-helpers');
                    await bindUser(userStore);
                    console.log('✅ User data binding completed');
                } catch (bindError) {
                    console.warn('⚠️ User data binding failed:', bindError);
                }

                console.log('✅ Authentication initialized: User authenticated');
            } else {
                console.log('ℹ️ Authentication initialized: No active session');
            }
        } catch (error) {
            console.warn('⚠️ Authentication initialization error:', error);
            console.log('ℹ️ Authentication initialization completed (no active session)');
        }
    });

    // EARLY FEATURES BINDING: If we have projectId and an authenticated user, bind features ASAP
    await timePhase('features_bootstrap', async () => {
        try {
            const { useUserStore } = await import('@/store/user.store');
            const { useProjectStore } = await import('@/store/project.store');
            const { useFeaturesStore } = await import('@/store/features.store');
            const userStore = useUserStore();
            const projectStore = useProjectStore();
            const featuresStore = useFeaturesStore();
            if (userStore.isAuthenticated && projectStore.projectId) {
                // Check if we have actual feature data, not just boundItems flag
                const hasFeatureData = Object.keys(featuresStore.features || {}).length > 0;

                if (!hasFeatureData) {
                    console.log('🔄 [Bootstrap] Loading features for project:', projectStore.projectId);
                    await featuresStore.bindFeaturesCollection(projectStore.projectId);

                    // Wait for features to actually populate
                    const start = Date.now();
                    const timeoutMs = 1000;
                    while (Object.keys(featuresStore.features || {}).length === 0 && Date.now() - start < timeoutMs) {
                        await new Promise(r => setTimeout(r, 50));
                    }

                    const featureCount = Object.keys(featuresStore.features || {}).length;
                    if (featureCount > 0) {
                        console.log(`✅ [Bootstrap] Features loaded early for project ${projectStore.projectId}: ${featureCount} features`);
                    } else {
                        console.warn(`⚠️ [Bootstrap] Features binding completed but no features loaded for project ${projectStore.projectId}`);
                    }
                } else {
                    console.log(`✅ [Bootstrap] Features already loaded for project ${projectStore.projectId}: ${hasFeatureData} features`);
                }
            }
        } catch (e) {
            console.warn('⚠️ [Bootstrap] Early features binding skipped:', e);
        }
    });

    // Initialize Phase 4 router guard system AFTER authentication
    console.log('🛡️ Initializing Phase 4 router guard system...');

    const { initializeSimplifiedRouterGuard } = await timePhase('import_guard', () => import('@/router/core/simplified-router-guard'));
    const guard = await timePhase('guard_init', async () => initializeSimplifiedRouterGuard(router));

    // PERFORMANCE FIX: Removed duplicate loader registration
    // SimplifiedRouterGuard already registers all loaders via allLoaders import
    // The glob-based discovery was causing duplicate "already registered, overwriting" warnings
    // and wasting ~50-100ms on redundant registration
    // If you need to add new loaders from /loaders/*.loader.ts, add them to the allLoaders array instead
    console.log('✅ Phase 4 router guard system initialized successfully');

    // Enable performance monitoring in development or when feature flag is on
    try {
        const { LOADING_FEATURES } = await timePhase('import_loading_features', () => import('@/config/loading-features'));
        if (LOADING_FEATURES.PERFORMANCE_TRACKING) {
            // Already imported earlier; ensure enabled
            if (!performanceMonitor.enabled) {
                performanceMonitor.enable();
                console.log('✅ Performance monitoring enabled');
            }
        }
    } catch (err) {
        console.warn('⚠️ Could not initialize performance monitoring:', err);
    }

    // Initialize NEW LOADING SYSTEM - Router Integration Phase 2
    try {
        console.log('🚀 Initializing Loading System - Router Integration...');
        const { LOADING_FEATURES } = await import('@/config/loading-features');

        if (LOADING_FEATURES.NEW_LOADING_SYSTEM || LOADING_FEATURES.ROUTER_LOADING_INTEGRATION || import.meta.env.DEV) {
            const { RouterLoadingManager } = await timePhase('import_router_loading_manager', () => import('@/router/core/router-loading-manager'));
            const loadingManager = await timePhase('router_loading_manager_init', async () => RouterLoadingManager.getInstance());

            // Initialize with router and existing guard system
            await timePhase('router_loading_manager_bind', async () => loadingManager.initialize(router));

            console.log('✅ Loading System Router Integration initialized successfully');

            // Add to global debug interface
            if (typeof window !== 'undefined') {
                (window as any).loadingManager = loadingManager;
                console.log('🔧 Loading Manager available globally: window.loadingManager');
            }
        } else {
            console.log('ℹ️ Loading System Router Integration skipped (feature flag disabled)');
        }
    } catch (error) {
        console.warn('⚠️ Loading System Router Integration failed to initialize:', error);
    }

    // Initialize Phase 4 debugging interface
    console.log('🚀 Setting up Phase 4 debugging interface...');
    if (typeof window !== 'undefined') {
        (window as any).phase4 = {
            test: () => {
                console.log('🚀 Phase 4 System Test');
                console.log('===============================');

                // Test router guard
                const guardInstance = guard?.getGuardChain();
                console.log('Router Guard Chain:', guardInstance);

                // Test data loader registry
                const dataRegistry = guard?.getDataLoaderRegistry();
                console.log('Data Loader Registry:', dataRegistry);

                console.log('✅ Phase 4 systems are operational');
                return {
                    guard: !!guardInstance,
                    dataRegistry: !!dataRegistry
                };
            },
            router: {
                // Add router access for testing navigation
                push: (route: string) => router.push(route),
                go: (delta: number) => router.go(delta),
                back: () => router.back(),
                current: () => router.currentRoute.value,
                guard: () => guard
            }
        };
        console.log('✅ Phase 4 debugging interface ready at window.phase4');
        console.log('💡 Router helpers: window.phase4.router');
        console.log('🧪 System test: window.phase4.test()');

        // Initialize unified debug tools for development
        console.log('🛠️ Setting up unified debug tools...');
        (window as any).debugTools = {
            showPanel: () => {
                console.log('🛠️ To open debug panel:');
                console.log('======================');
                console.log('1. Use the "Show Debug Tools" button (bottom-right corner)');
                console.log('2. Or call: window.showDebugTools()');
                console.log('');
                console.log('Available debug categories:');
                console.log('• App Variant - Switch between app variants (churches, crms, etc.)');
                console.log('• Project Type - Switch between white_label and private_label modes');
                console.log('• Router - Debug routing, navigation, and data loading');
                console.log('• RBAC - Test role-based access control and permissions');
                console.log('• Components - Debug specific components and maps');
                console.log('• System - General system debugging and utilities');
                console.log('======================');

                // Try to show the panel if the function is available
                if ((window as any).showDebugTools) {
                    (window as any).showDebugTools();
                } else {
                    console.warn('⚠️ Debug panel component not yet loaded. Trying to wait for it...');

                    // Retry a few times to wait for the component to load
                    let retries = 0;
                    const maxRetries = 10;
                    const interval = setInterval(() => {
                        retries++;
                        if ((window as any).showDebugTools) {
                            console.log('✅ Debug panel component loaded, opening panel...');
                            (window as any).showDebugTools();
                            clearInterval(interval);
                        } else if (retries >= maxRetries) {
                            console.error('❌ Debug panel component failed to load. Try refreshing the page or navigate to a different route.');
                            console.log('💡 Alternative: Look for the "Show Debug Tools" button in the bottom-right corner');
                            clearInterval(interval);
                        }
                    }, 500);
                }
            },

            // Quick access functions for common tasks
            switchProjectType: (type: 'white_label' | 'private_label') => {
                if ((window as any).projectTypeDebug?.switchTo) {
                    (window as any).projectTypeDebug.switchTo(type);
                } else {
                    console.warn('Project type debug tools not yet available. Try: window.debugTools.showPanel()');
                }
            },

            switchAppVariant: (variant: string) => {
                if ((window as any).appVariantDebug?.switchVariant) {
                    (window as any).appVariantDebug.switchVariant(variant);
                } else {
                    console.warn('App variant debug tools not yet available. Try: window.debugTools.showPanel()');
                }
            },

            logAllDebugInfo: () => {
                console.group('=== All Debug Information ===');

                // System info
                if ((window as any).systemDebug?.getSystemInfo) {
                    console.log('System Info:', (window as any).systemDebug.getSystemInfo());
                }

                // Project type info
                if ((window as any).projectTypeDebug?.getCurrent) {
                    console.log('Project Type:', (window as any).projectTypeDebug.getCurrent());
                }

                // App variant info
                if ((window as any).appVariantDebug?.getCurrentVariant) {
                    console.log('App Variant:', (window as any).appVariantDebug.getCurrentVariant());
                }

                // Router info
                if ((window as any).routerDebug) {
                    console.log('Router Debug:', (window as any).routerDebug);
                }

                // RBAC info
                if ((window as any).getRBACDebugInfo) {
                    console.log('RBAC Info:', (window as any).getRBACDebugInfo());
                }

                console.groupEnd();
            },

            help: () => {
                console.log('🛠️ Debug Tools Help');
                console.log('==================');
                console.log('');
                console.log('⌨️ Keyboard Shortcuts:');
                console.log('  Option+Shift+D (Alt+Shift+D on Windows) - Open debug panel');
                console.log('  Option+Shift+` or Ctrl+Shift+` - Alternative shortcuts');
                console.log('  Note: Option key avoids conflicts with browser debug tools');
                console.log('');
                console.log('🔧 Main Commands:');
                console.log('  window.debugTools.showPanel() - Open debug panel');
                console.log('  window.showDebugTools() - Show debug panel');
                console.log('  window.hideDebugTools() - Hide debug panel');
                console.log('');
                console.log('⚡ Quick Actions:');
                console.log("  window.debugTools.switchProjectType('white_label')");
                console.log("  window.debugTools.switchProjectType('private_label')");
                console.log("  window.debugTools.switchAppVariant('churches')");
                console.log('  window.debugTools.logAllDebugInfo()');
                console.log('');
                console.log('📋 Individual Tools:');
                console.log('  window.projectTypeDebug - Project type switching');
                console.log('  window.appVariantDebug - App variant switching');
                console.log('  window.routerDebug - Router debugging');
                console.log('  window.rbacDebugTab - RBAC testing');
                console.log('  window.componentDebug - Component debugging');
                console.log('  window.systemDebug - System utilities');
                console.log('');
                console.log('💡 Pro tip: Open the debug panel for a visual interface!');
            }
        };

        console.log('✅ Unified debug tools ready');
        console.log('💡 Quick start: window.debugTools.help()');
    }

    // Wait until the router is fully ready (i.e. async components, hooks, etc., are resolved)
    await timePhase('router_ready', async () => router.isReady());

    // CRITICAL: Manually trigger data loading for initial route since beforeEach may not fire
    const initialRoute = router.currentRoute.value;

    if (initialRoute && initialRoute.name) {
        console.log('🔧 Manually triggering data loaders for initial route:', initialRoute.name);
        try {
            // Pass the router instance explicitly to ensure routes are available
            await timePhase('initial_route', async () => guard.handleInitialRoute(initialRoute, router));
        } catch (error) {
            console.warn('⚠️ Initial route data loading failed:', error);
        }
    }

    const sdk = getSdk();
    sdk?.onSessionTokenChange((newSession) => {
        // here you can implement custom logic when the session is changing
        if (newSession) {
            console.log('Descope session token changed. New token available.');
            // You can add code here to automatically authenticate the user
            // with your backend if needed, or update the user's state

            // In a real implementation, you might want to call a function to 
            // validate the token with your backend or update the user state
        } else {
            console.log('Descope session token removed. User is logged out.');
            // Handle logout if needed
        }
    });

    // Mount the app
    await timePhase('mount_app', async () => {
        app.mount("#app");
        // Signal early detection script that Vue has mounted
        // This helps prevent false positives when Vue mounts but content hasn't rendered yet
        if (typeof window !== 'undefined') {
            (window as any).__vueMounted = true;
            // Also mark body to help early detection
            document.body.setAttribute('data-vue-mounted', 'true');
            // Call signal function if available
            if (typeof (window as any).__signalVueMounted === 'function') {
                (window as any).__signalVueMounted();
            }
        }
    });

    // Initialize Loading Coordinator for better HTML/Vue loading coordination
    // CRITICAL: Initialize early and with multiple fallbacks
    try {
        const { loadingCoordinator } = await timePhase('import_loading_coordinator', () => import('@/utils/loading-coordinator'));

        // Initialize with proper error handling
        try {
            await timePhase('loading_coordinator_init', async () => loadingCoordinator.initialize(router));
            console.log('✅ Loading Coordinator initialized successfully');
        } catch (initError) {
            console.warn('⚠️ Loading Coordinator initialization partially failed:', initError);
            // Continue anyway - the coordinator can still hide HTML loading
        }

        // Do NOT immediately hide the initial loading screen on mount anymore.
        // The coordinator will hide it when the loading phase reaches PAGE_READY,
        // ensuring HTML loader stays during long app bootstrap on slow devices.

        // Make available globally for debugging
        if (typeof window !== 'undefined') {
            (window as any).loadingCoordinator = loadingCoordinator;
            console.log('🔧 Loading Coordinator available globally: window.loadingCoordinator');
        }
    } catch (error) {
        console.error('❌ Loading Coordinator import failed completely:', error);
        // Comprehensive fallback
        document.body.classList.add('app-loaded');
        const loadingEl = document.getElementById('initial-loading');
        if (loadingEl) loadingEl.style.display = 'none';
        console.warn('⚠️ Used emergency fallback to hide loading screen');
    }

    // Initialize Root Cause Detector for proactive issue prevention (development only by default)
    if (import.meta.env.DEV) {
        try {
            const { rootCauseDetector } = await timePhase('import_root_cause_detector', () => import('@/utils/root-cause-detector'));
            await timePhase('root_cause_start', async () => rootCauseDetector.startMonitoring());

            // Make available globally for debugging
            if (typeof window !== 'undefined') {
                (window as any).rootCauseDetector = rootCauseDetector;
                console.log('🔍 Root Cause Detector available globally: window.rootCauseDetector');
            }
        } catch (error) {
            console.warn('⚠️ Root Cause Detector failed to initialize:', error);
        }
    }

    // Make router available globally for debugging
    if (typeof window !== 'undefined') {
        (window as any).router = router;
    }

    // Set app as ready after mounting and router initialization
    const { useApplicationStore } = await import('@/store/application.store');
    const applicationStore = useApplicationStore();

    // Check if we're on an initial route that needs data loading
    const currentRouteAfterMount = router.currentRoute.value;
    if (currentRouteAfterMount && currentRouteAfterMount.name) {
        // Navigation has occurred, let the guard system handle loading states
        console.log('✅ App mounted, initial navigation to:', currentRouteAfterMount.name);
        console.log('🔄 Router guard system will handle app and page ready states');
    } else {
        // No navigation yet, set both app and page ready manually
        applicationStore.setState({ key: 'isAppReady', value: true });
        applicationStore.setState({ key: 'isPageReady', value: true });
        console.log('✅ App mounted and marked as ready (no initial navigation)');
    }

    // END OF BOOTSTRAP: wait until app is reported ready (or timeout), then end timer
    try {
        const waitForAppReady = async (timeoutMs = 15000) => {
            if (applicationStore.isAppReady) return;
            await new Promise<void>((resolve) => {
                const start = performance.now();
                const interval = window.setInterval(() => {
                    if (applicationStore.isAppReady || performance.now() - start > timeoutMs) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);
            });
        };
        await waitForAppReady();
        performanceMonitor.endTiming(__bootstrapTimingId || 'app_bootstrap', true);
    } catch (e) {
        performanceMonitor.endTiming(__bootstrapTimingId || 'app_bootstrap', false, (e as any)?.message || String(e));
    }

    // Initialize Analytics Service (connects to GTM/GA4 via gtag)
    try {
        console.log('📊 Initializing Analytics Service...');
        const { analyticsService } = await import('@/services/analytics/analyticsService');
        
        // Get measurement ID from environment variable or fallback to window global (set in index.html)
        const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined
            || (typeof window !== 'undefined' ? (window as any).__GA4_MEASUREMENT_ID__ : undefined);
        
        // Wait for gtag to be available (loaded directly in index.html)
        const waitForGtag = async (maxWaitMs = 5000): Promise<boolean> => {
            const startTime = Date.now();
            while (Date.now() - startTime < maxWaitMs) {
                if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                    return true;
                }
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return false;
        };
        
        const gtagAvailable = await waitForGtag();
        
        if (gtagAvailable && measurementId) {
            analyticsService.initialize(measurementId, {
                debugMode: import.meta.env.DEV,
            });
            console.log('✅ Analytics Service initialized with measurement ID:', measurementId);
            
            // Set up project context if available
            const { useProjectStore } = await import('@/store/project.store');
            const { useUserStore } = await import('@/store/user.store');
            const projectStore = useProjectStore();
            const userStore = useUserStore();
            
            if (projectStore.projectId) {
                analyticsService.setProjectContext(projectStore.projectId);
                console.log('📊 Analytics project context set:', projectStore.projectId);
            }
            
            if (userStore.isAuthenticated && userStore.authUser?.uid) {
                analyticsService.setUserContext(userStore.authUser.uid, {
                    user_role: userStore.projectRole || undefined,
                    project_id: projectStore.projectId || undefined,
                });
                console.log('📊 Analytics user context set:', userStore.authUser.uid);
            }
            
            // Make analytics service available globally for debugging
            if (typeof window !== 'undefined') {
                (window as any).analyticsService = analyticsService;
                console.log('🔧 Analytics Service available globally: window.analyticsService');
            }
        } else {
            if (!gtagAvailable) {
                console.warn('⚠️ [Analytics] gtag not available - analytics events will be queued');
            }
            if (!measurementId) {
                console.warn('⚠️ [Analytics] No measurement ID available (check VITE_FIREBASE_MEASUREMENT_ID or index.html)');
            }
        }
    } catch (error) {
        console.warn('⚠️ Analytics Service initialization failed:', error);
    }

    // Final development setup after app mount
    if (import.meta.env.DEV) {
        console.log('🎯 Development Mode Active');
        console.log('========================');
        console.log('🛠️ Debug tools available:');
        console.log('  • window.debugTools.help() - Show all debug commands');
        console.log('  • window.debugTools.showPanel() - Open debug panel');
        console.log('  • window.showDebugTools() - Alternative to show panel');
        console.log('');
        console.log('⌨️ Keyboard shortcuts:');
        console.log('  • Option+Shift+D (Alt+Shift+D) - Open debug panel');
        console.log('  • Option+Shift+` or Ctrl+Shift+` - Alternative shortcuts');
        console.log('');
        console.log('⚡ Quick project type switching:');
        console.log("  • window.debugTools.switchProjectType('white_label')");
        console.log("  • window.debugTools.switchProjectType('private_label')");
        console.log('');
        console.log('💡 Look for the "Show Debug Tools" button in bottom-right corner!');
        console.log('========================');
    }
}

// Call the initialize function
initializeApp();
